import { useEffect, useRef, useState } from "react";

// --- Sprite geometry (berry-run-sprite.webp = 16 frames, 239x150 each) ---
const FRAMES = 16;
const NATIVE_FW = 239;
const NATIVE_FH = 150;
const DISPLAY_H = 116;
const SCALE = DISPLAY_H / NATIVE_FH;
const FW = Math.round(NATIVE_FW * SCALE);
const TOTAL_W = FW * FRAMES;

const TRAVEL_MS = 3300; // desktop: time to cross the screen
const TRAVEL_MS_MOBILE = 1600; // phone: cross faster (legs stay the same!)
const LEG_MS = 640; // natural gait cadence (16 frames @ ~24fps) — same on all devices

const RUN_COOLDOWN_MS = 4000; // rest between runs
const SCROLL_DELTA = 600; // px the user must scroll before the next run
const MAX_RUNS = 2; // only run twice per page load, then stop until reload

// Berry trots across the bottom as the user scrolls, alternating direction:
// first right -> left (faces left, the video orientation), then left -> right
// (mirrored so she faces right), and so on.
export const BerryRunner = () => {
  const [run, setRun] = useState(null); // { id, dir }
  const idRef = useRef(0);
  const dirRef = useRef("rtl"); // first run goes right -> left
  const runningRef = useRef(false);
  const lastRunAtRef = useRef(0);
  const lastTriggerYRef = useRef(0);
  const runCountRef = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      if (runCountRef.current >= MAX_RUNS) return;
      if (runningRef.current) return;
      if (Date.now() - lastRunAtRef.current < RUN_COOLDOWN_MS) return;
      const y = window.scrollY;
      if (Math.abs(y - lastTriggerYRef.current) < SCROLL_DELTA) return;

      lastTriggerYRef.current = y;
      runningRef.current = true;
      runCountRef.current += 1;
      idRef.current += 1;
      const dir = dirRef.current;
      dirRef.current = dir === "rtl" ? "ltr" : "rtl"; // alternate next time
      setRun({ id: idRef.current, dir });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleEnd = () => {
    runningRef.current = false;
    lastRunAtRef.current = Date.now();
    setRun(null);
  };

  if (!run) return null;
  const ltr = run.dir === "ltr";
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
  const travelMs = isMobile ? TRAVEL_MS_MOBILE : TRAVEL_MS;

  return (
    <div
      key={run.id}
      data-testid="berry-runner"
      className="berry-runner-wrap"
      style={{
        animationName: ltr ? "berry-travel-ltr" : "berry-travel-rtl",
        animationDuration: `${travelMs}ms`,
        animationTimingFunction: "linear",
        animationFillMode: "forwards",
      }}
      onAnimationEnd={handleEnd}
    >
      <div
        className="berry-runner-sprite"
        style={{
          width: `${FW}px`,
          height: `${DISPLAY_H}px`,
          backgroundImage: "url(/generated/berry-run-sprite.webp)",
          backgroundSize: `${TOTAL_W}px ${DISPLAY_H}px`,
          transform: ltr ? "scaleX(-1)" : "none",
          animation: `berry-legs ${LEG_MS}ms steps(${FRAMES}) infinite`,
          "--legs-end": `-${TOTAL_W}px`,
        }}
      />
    </div>
  );
};

export default BerryRunner;
